import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import WorkOrders from './pages/WorkOrders';
import WorkOrderDetail from './pages/WorkOrderDetail';
import Technicians from './pages/Technicians';
import Parts from './pages/Parts';
import Invoices from './pages/Invoices';
import AIAssistant from './pages/AIAssistant';
import MapView from './pages/MapView';
import Billing from './pages/Billing';
import Upgrade from './pages/Upgrade';
import AdminDashboard from './pages/AdminDashboard';
import Roles from './pages/system/Roles';
import Permissions from './pages/system/Permissions';
import TechJobNotes from './pages/tech/TechJobNotes';
import TechJobPhotos from './pages/tech/TechJobPhotos';
import TechJobParts from './pages/tech/TechJobParts';
import TechCompleteJob from './pages/tech/TechCompleteJob';
// Technician mobile pages
import TechJobs from './pages/tech/TechJobs';
import TechJobDetail from './pages/tech/TechJobDetail';
import GuidePage from './pages/GuidePage';

import { PermissionProvider, usePermissions } from './contexts/PermissionContext';

const isAuthenticated = () => !!localStorage.getItem('token');

// Protect a route by permission code.
// - While permissions are loading: shows a blank screen (prevents flash).
// - If the user lacks the permission: redirects to /dashboard.
// - If no redirectTo is given (e.g. for /dashboard itself): shows an access-denied message
//   so there is no redirect loop when the user has no permissions at all.
function PermRoute({ permission, children, redirectTo = '/dashboard' }) {
  const { hasPermission, loading } = usePermissions();

  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (loading) return null;

  if (!hasPermission(permission)) {
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            Access Denied
          </p>
          <p style={{ fontSize: 14 }}>
            You do not have permission to view this page.
          </p>
        </div>
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
}

// Admin-only system pages (role-based, enforced by require_saas_admin on the backend)
function AdminRoute({ children }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

// Technician mobile routes
function TechRoute({ children }) {
  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // 你如果想限制只有 technician 能进手机端，可以保留这段
  // 如果 manager / dispatcher 也想测试手机端，可以先注释掉
  if (!['technician', 'manager', 'dispatcher', 'admin'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <PermissionProvider>
        <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Dashboard — no redirectTo so we show "Access Denied" instead of looping */}
          <Route
            path="/dashboard"
            element={
              <PermRoute permission="dashboard:view" redirectTo={null}>
                <Dashboard />
              </PermRoute>
            }
          />

          <Route
            path="/work-orders"
            element={
              <PermRoute permission="work_orders:view">
                <WorkOrders />
              </PermRoute>
            }
          />

          <Route
            path="/work-orders/:id"
            element={
              <PermRoute permission="work_orders:view">
                <WorkOrderDetail />
              </PermRoute>
            }
          />

          {/* Technician mobile routes */}
          <Route
            path="/tech/jobs"
            element={
              <TechRoute>
                <TechJobs />
              </TechRoute>
            }
          />

          <Route
            path="/tech/jobs/:id"
            element={
              <TechRoute>
                <TechJobDetail />
              </TechRoute>
            }
          />
          <Route 
            path="/tech/jobs/:id/notes" 
            element={
              <TechRoute>
                <TechJobNotes />
              </TechRoute>
           } 
         />
          <Route
            path="/tech/jobs/:id/parts"
            element={
              <TechRoute>
                <TechJobParts />
              </TechRoute>
            }
          />         
          <Route
            path="/tech/jobs/:id/photos"
            element={
              <TechRoute>
                <TechJobPhotos />
              </TechRoute>
            }
          />
          <Route
            path="/tech/jobs/:id/complete"
            element={
              <TechRoute>
                <TechCompleteJob />
              </TechRoute>
            }
          />          
          <Route
            path="/technicians"
            element={
              <PermRoute permission="technicians:view">
                <Technicians />
              </PermRoute>
            }
          />

          <Route
            path="/map"
            element={
              <PermRoute permission="technicians:map:view">
                <MapView />
              </PermRoute>
            }
          />

          <Route
            path="/parts"
            element={
              <PermRoute permission="parts:view">
                <Parts />
              </PermRoute>
            }
          />

          <Route
            path="/invoices"
            element={
              <PermRoute permission="invoices:view">
                <Invoices />
              </PermRoute>
            }
          />

          <Route
            path="/ai-assistant"
            element={
              <PermRoute permission="ai_assistant:view">
                <AIAssistant />
              </PermRoute>
            }
          />

          <Route
            path="/billing"
            element={
              <PermRoute permission="billing:view">
                <Billing />
              </PermRoute>
            }
          />

          <Route
            path="/upgrade"
            element={
              <PermRoute permission="billing:view">
                <Upgrade />
              </PermRoute>
            }
          />

          {/* System pages — admin role required (backend enforces require_saas_admin) */}
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          <Route
            path="/system/roles"
            element={
              <AdminRoute>
                <Roles />
              </AdminRoute>
            }
          />

          <Route
            path="/system/permissions"
            element={
              <AdminRoute>
                <Permissions />
              </AdminRoute>
            }
          />

          {/* User Guide — accessible to all authenticated roles */}
          <Route
            path="/guide"
            element={
              <PermRoute permission="dashboard:view" redirectTo={null}>
                <GuidePage />
              </PermRoute>
            }
          />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </PermissionProvider>
    </BrowserRouter>
  );
}